/**
 * ====================================================================
 * NotificationPopup Component
 * ====================================================================
 * 
 * Компонент для показа попапов уведомлений в реальном времени
 * Появляется сверху экрана, автоматически исчезает через несколько секунд
 */

import React, { useEffect, useState } from 'react';
import './NotificationPopup.css';
import { Icon } from './Icon';

interface Notification {
  id: number;
  title: string;
  message: string;
  amount?: number;
  notification_type: string;
  instance_date?: string;
  scheduled_time?: string;
}

interface NotificationPopupProps {
  notification: Notification | null;
  duration?: number; // миллисекунды
  onClose?: () => void;
}

export const NotificationPopup: React.FC<NotificationPopupProps> = ({
  notification,
  duration = 5000,
  onClose
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [currentNotification, setCurrentNotification] = useState<Notification | null>(null);

  useEffect(() => {
    if (notification) {
      setCurrentNotification(notification);
      setIsVisible(true);

      // Автоматическое закрытие через duration мс
      const timer = setTimeout(() => {
        handleClose();
      }, duration);

      return () => clearTimeout(timer);
    }
  }, [notification, duration]);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => {
      setCurrentNotification(null);
      if (onClose) {
        onClose();
      }
    }, 300); // Задержка для анимации
  };

  if (!currentNotification) {
    return null;
  }

  // Определяем иконку и цвет в зависимости от типа уведомления
  const getNotificationIcon = (type: string): { name: string; color: string } => {
    if (type.includes('income')) return { name: 'dollar-sign', color: '#34C759' };
    if (type.includes('expense')) return { name: 'credit-card', color: '#FF3B30' };
    if (type.includes('goal')) return { name: 'target', color: '#667eea' };
    if (type.includes('reminder')) return { name: 'bell', color: '#FF9500' };
    if (type === 'limit_warning') {
      // Используем разные иконки в зависимости от уровня превышения
      const level = currentNotification.metadata?.level;
      return { 
        name: level === 'exceeded' ? 'alert-circle' : 'alert-triangle',
        color: level === 'exceeded' ? '#FF3B30' : '#FF9500'
      };
    }
    return { name: 'clipboard', color: '#007AFF' };
  };

  const getNotificationColor = (type: string) => {
    if (type.includes('income')) return '#4CAF50';
    if (type.includes('expense')) return '#F44336';
    if (type.includes('goal')) return '#2196F3';
    if (type.includes('reminder')) return '#FF9800';
    if (type === 'limit_warning') {
      // Красный для превышения, оранжевый для предупреждения
      const level = currentNotification.metadata?.level;
      return level === 'exceeded' ? '#F44336' : '#FF9800';
    }
    return '#9C27B0';
  };

  const iconData = getNotificationIcon(currentNotification.notification_type);
  const color = getNotificationColor(currentNotification.notification_type);

  return (
    <div className={`notification-popup ${isVisible ? 'visible' : 'hidden'}`}>
      <div className="notification-popup-content" style={{ borderLeftColor: color }}>
        <button className="notification-popup-close" onClick={handleClose}>
          ✕
        </button>
        
        <div className="notification-popup-icon">
          <Icon name={iconData.name} size={24} color={iconData.color} />
        </div>
        
        <div className="notification-popup-text">
          <div className="notification-popup-title">{currentNotification.title}</div>
          <div className="notification-popup-message">{currentNotification.message}</div>
          
          {currentNotification.amount && (
            <div className="notification-popup-amount">
              {currentNotification.amount.toLocaleString('ru-RU')} ₽
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

